import { type EnvironmentId, type ThreadId } from "@cmmd-forge/contracts";
import { memo, useCallback, useMemo, useState } from "react";
import { useComposerDraftStore } from "../../composerDraftStore";
import { type TerminalContextSelection } from "../../lib/terminalContext";
import { useStore } from "../../store";
import { createProjectSelectorByRef, createThreadSelectorByRef } from "../../storeSelectors";
import { selectThreadTerminalState, useTerminalStateStore } from "../../terminalStateStore";
import { useThreadTerminalSessionControls } from "./useThreadTerminalSessionControls";
import {
  derivePersistentTerminalDrawerViewModel,
  resolveActivePersistentTerminalLaunchContext,
  resolvePersistentTerminalCwd,
  resolvePersistentTerminalProjectRef,
  resolvePersistentTerminalRuntimeEnv,
  resolvePersistentTerminalWorktreePath,
} from "../ChatView.logic";
import ThreadTerminalDrawer from "../ThreadTerminalDrawer";

export interface TerminalLaunchContext {
  readonly threadId: ThreadId;
  readonly terminalId?: string;
  readonly cwd: string;
  readonly worktreePath: string | null;
  readonly runtimeEnv?: Record<string, string>;
}

type PersistentTerminalLaunchContext = Pick<
  TerminalLaunchContext,
  "terminalId" | "cwd" | "worktreePath" | "runtimeEnv"
>;

interface PersistentThreadTerminalDrawerProps {
  readonly threadRef: { readonly environmentId: EnvironmentId; readonly threadId: ThreadId };
  readonly threadId: ThreadId;
  readonly visible: boolean;
  readonly launchContext: PersistentTerminalLaunchContext | null;
  readonly focusRequestId: number;
  readonly splitShortcutLabel: string | undefined;
  readonly newShortcutLabel: string | undefined;
  readonly closeShortcutLabel: string | undefined;
  readonly onAddTerminalContext: (selection: TerminalContextSelection) => void;
}

export const PersistentThreadTerminalDrawer = memo(function PersistentThreadTerminalDrawer({
  threadRef,
  threadId,
  visible,
  launchContext,
  focusRequestId,
  splitShortcutLabel,
  newShortcutLabel,
  closeShortcutLabel,
  onAddTerminalContext,
}: PersistentThreadTerminalDrawerProps) {
  const serverThread = useStore(useMemo(() => createThreadSelectorByRef(threadRef), [threadRef]));
  const draftThread = useComposerDraftStore((store) => store.getDraftThreadByRef(threadRef));
  const projectRef = useMemo(
    () => resolvePersistentTerminalProjectRef({ serverThread, draftThread }),
    [draftThread, serverThread],
  );
  const project = useStore(useMemo(() => createProjectSelectorByRef(projectRef), [projectRef]));
  const terminalState = useTerminalStateStore((state) =>
    selectThreadTerminalState(state.terminalStateByThreadKey, threadRef),
  );
  const activeLaunchContext = resolveActivePersistentTerminalLaunchContext({
    launchContext,
    activeTerminalId: terminalState.activeTerminalId,
  });
  const storeSetTerminalHeight = useTerminalStateStore((state) => state.setTerminalHeight);
  const [localFocusRequestId, setLocalFocusRequestId] = useState(0);
  const projectCwd = project?.cwd ?? null;
  const effectiveWorktreePath = useMemo(
    () => resolvePersistentTerminalWorktreePath({ activeLaunchContext, serverThread, draftThread }),
    [activeLaunchContext, draftThread, serverThread],
  );
  const cwd = useMemo(
    () =>
      resolvePersistentTerminalCwd({
        activeLaunchContext,
        projectCwd,
        worktreePath: effectiveWorktreePath,
      }),
    [activeLaunchContext, effectiveWorktreePath, projectCwd],
  );
  const runtimeEnv = useMemo(
    () =>
      resolvePersistentTerminalRuntimeEnv({
        activeLaunchContext,
        projectCwd,
        worktreePath: effectiveWorktreePath,
      }),
    [activeLaunchContext, effectiveWorktreePath, projectCwd],
  );

  const bumpFocusRequestId = useCallback(() => {
    if (!visible) {
      return;
    }
    setLocalFocusRequestId((value) => value + 1);
  }, [visible]);

  const setTerminalHeight = useCallback(
    (height: number) => {
      storeSetTerminalHeight(threadRef, height);
    },
    [storeSetTerminalHeight, threadRef],
  );

  // The drawer is the one host whose own sessions open it: the panel's
  // Terminal tab opts out of exactly that through the same controls.
  const controls = useThreadTerminalSessionControls({
    threadRef,
    threadId,
    sessionIds: terminalState.terminalIds,
    opensDrawerOnCreate: true,
    onAfterAction: bumpFocusRequestId,
  });

  const handleAddTerminalContext = useCallback(
    (selection: TerminalContextSelection) => {
      if (!visible) {
        return;
      }
      onAddTerminalContext(selection);
    },
    [onAddTerminalContext, visible],
  );

  const drawerViewModel = derivePersistentTerminalDrawerViewModel({
    hasProject: Boolean(project),
    terminalOpen: terminalState.terminalOpen,
    hasCwd: Boolean(cwd),
    visible,
    parentFocusRequestId: focusRequestId,
    localFocusRequestId,
    splitShortcutLabel,
    newShortcutLabel,
    closeShortcutLabel,
  });

  if (!drawerViewModel.shouldRender || !project || !cwd) {
    return null;
  }

  return (
    <div className={drawerViewModel.wrapperClassName}>
      <ThreadTerminalDrawer
        threadRef={threadRef}
        threadId={threadId}
        cmmdProjectId={project.cmmdRegistration?.cmmdProjectId ?? null}
        cwd={cwd}
        worktreePath={effectiveWorktreePath}
        runtimeEnv={runtimeEnv}
        visible={visible}
        height={terminalState.terminalHeight}
        terminalIds={terminalState.terminalIds}
        activeTerminalId={terminalState.activeTerminalId}
        terminalGroups={terminalState.terminalGroups}
        activeTerminalGroupId={terminalState.activeTerminalGroupId}
        focusRequestId={drawerViewModel.focusRequestId}
        onSplitTerminal={controls.splitTerminal}
        onNewTerminal={controls.createTerminal}
        splitShortcutLabel={drawerViewModel.splitShortcutLabel}
        newShortcutLabel={drawerViewModel.newShortcutLabel}
        closeShortcutLabel={drawerViewModel.closeShortcutLabel}
        onActiveTerminalChange={controls.activateTerminal}
        onCloseTerminal={controls.closeTerminal}
        onCloseAllTerminals={controls.closeAllTerminals}
        onHeightChange={setTerminalHeight}
        onAddTerminalContext={handleAddTerminalContext}
      />
    </div>
  );
});
